import React, { useEffect, useState } from 'react';
import { Users, AlertTriangle, Inbox } from 'lucide-react';
import { mccGet } from './mccApi';
import { toast } from 'sonner';
import { SectionHeader, EmptyState, Avatar, StatusPill } from './mccUi';
import MccContentCard from './MccContentCard';

export default function MccTeamView({ team, onOpenProject }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const nameOf = (uid) => (team.find((t) => t.id === uid) || {}).name || uid;

  const load = async () => {
    setLoading(true);
    try { setData(await mccGet('/marketing/team-view')); }
    catch (e) { toast.error(e.message || 'Failed to load team view'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading || !data) return <div className="p-6 text-sm text-slate-500">Loading…</div>;

  return (
    <div className="p-6 space-y-4">
      <SectionHeader
        title="Manager Team View"
        subtitle="Everyone's active workload, overdue items, approvals and bottlenecks in one dashboard."
        icon={Users}
      />

      {/* Bottlenecks + Unassigned */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Section title="Bottlenecks (5+ days idle)" icon={AlertTriangle} tone="rose"
          items={data.bottlenecks} nameOf={nameOf} onOpenProject={onOpenProject} emptyText="No stalled projects — nice." />
        <Section title="Unassigned projects" icon={Inbox} tone="amber"
          items={data.unassigned} nameOf={nameOf} onOpenProject={onOpenProject} emptyText="Everything has an owner." />
      </div>

      {/* Per person */}
      <div className="grid lg:grid-cols-2 gap-4">
        {(data.per_person || []).map(({ user, assigned, overdue, awaiting_approval, ready_to_publish, completed_this_month, changes_required }) => (
          <div key={user.id} className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="flex items-center gap-3">
              <Avatar name={user.name} size={40} />
              <div className="min-w-0 flex-1">
                <p className="text-base font-bold text-slate-900">{user.name}</p>
                <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">{user.role}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold tabular-nums text-indigo-700">{assigned.length}</p>
                <p className="text-[10px] uppercase text-slate-500 tracking-wider">assigned</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
              <MiniStat label="Overdue" value={overdue.length} tone="rose" />
              <MiniStat label="Awaiting approval" value={awaiting_approval.length} tone="amber" />
              <MiniStat label="Ready to publish" value={ready_to_publish.length} tone="emerald" />
              <MiniStat label="Changes required" value={changes_required.length} tone="rose" />
            </div>
            {assigned.length === 0 ? (
              <p className="text-xs text-slate-500 text-center py-4">No active projects.</p>
            ) : (
              <div className="mt-3 space-y-1.5 max-h-[220px] overflow-y-auto pr-1">
                {assigned.slice(0, 4).map((p) => (
                  <button key={p.id} onClick={() => onOpenProject(p.id)} className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-50 border border-slate-100">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-800 truncate flex-1">{p.title}</span>
                      <StatusPill status={p.status} size="sm" />
                    </div>
                  </button>
                ))}
                {assigned.length > 4 && <p className="text-[10px] text-slate-500 text-center">+{assigned.length - 4} more</p>}
              </div>
            )}
            {completed_this_month.length > 0 && (
              <p className="text-[10px] text-emerald-700 mt-2 font-semibold">✓ {completed_this_month.length} completed this month</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, tone, items, nameOf, onOpenProject, emptyText }) {
  const toneMap = { rose: 'border-rose-200 bg-rose-50', amber: 'border-amber-200 bg-amber-50' };
  return (
    <div className={`rounded-xl border p-3 ${toneMap[tone] || 'border-slate-200 bg-white'}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4" />
        <p className="text-sm font-bold text-slate-800">{title}</p>
        <span className="text-[10px] font-bold text-slate-500 px-1.5 py-0.5 rounded bg-white">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-slate-500 text-center py-4">{emptyText}</p>
      ) : (
        <div className="space-y-1.5 max-h-[340px] overflow-y-auto">
          {items.slice(0, 8).map((p) => (
            <MccContentCard key={p.id} item={p} nameOf={nameOf} onClick={() => onOpenProject(p.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, tone }) {
  const bg = { rose: 'bg-rose-50 text-rose-800', amber: 'bg-amber-50 text-amber-800', emerald: 'bg-emerald-50 text-emerald-800' }[tone] || 'bg-slate-50 text-slate-800';
  return (
    <div className={`rounded px-2 py-1.5 ${bg}`}>
      <p className="text-lg font-bold tabular-nums">{value}</p>
      <p className="text-[9px] uppercase tracking-wider font-bold opacity-75">{label}</p>
    </div>
  );
}
